/**
 * Rate Limiting Service
 * Limits AI usage to 300 questions per day to prevent abuse
 * Stores data in LocalStorage with daily reset
 */

export interface RateLimitStatus {
  questionsUsed: number;
  questionsRemaining: number;
  isLimited: boolean;
  resetTime: string;
  isUnlocked: boolean;
}

export class RateLimitService {
  private static instance: RateLimitService;
  private readonly STORAGE_KEY = 'ot_ai_rate_limit';
  private readonly UNLOCK_KEY = 'ot_ai_unlocked';
  private readonly DAILY_LIMIT = 500; // Increased from 300 - API keys secured with Vercel Functions
  private readonly UNLOCK_PASSWORD = 'maria2025'; // Change this to whatever you want

  private constructor() {
    console.log('🔒 RateLimitService initialized');
  }

  public static getInstance(): RateLimitService {
    if (!RateLimitService.instance) {
      RateLimitService.instance = new RateLimitService();
    }
    return RateLimitService.instance;
  }

  /**
   * Check if user can make a request
   */
  public canMakeRequest(): boolean {
    // Check if unlocked first
    if (this.isUnlocked()) {
      return true;
    }

    const data = this.getRateLimitData();

    // Check if we need to reset (new day)
    if (this.shouldReset(data.date)) {
      this.reset();
      return true;
    }

    return data.count < this.DAILY_LIMIT;
  }

  /**
   * Record a request (increment counter)
   */
  public recordRequest(): void {
    if (this.isUnlocked()) {
      return; // Don't count if unlocked
    }

    const data = this.getRateLimitData();

    // Reset if new day
    if (this.shouldReset(data.date)) {
      this.reset();
      data.count = 0;
      data.date = this.getTodayString();
    }

    data.count += 1;
    this.saveRateLimitData(data);
  }

  /**
   * Get current rate limit status
   */
  public getStatus(): RateLimitStatus {
    const isUnlocked = this.isUnlocked();

    if (isUnlocked) {
      return {
        questionsUsed: 0,
        questionsRemaining: Infinity,
        isLimited: false,
        resetTime: 'Never (Unlocked)',
        isUnlocked: true
      };
    }

    const data = this.getRateLimitData();

    // Reset if new day
    if (this.shouldReset(data.date)) {
      this.reset();
      data.count = 0;
      data.date = this.getTodayString();
    }

    const questionsUsed = data.count;
    const questionsRemaining = Math.max(0, this.DAILY_LIMIT - data.count);
    const isLimited = data.count >= this.DAILY_LIMIT;
    const resetTime = this.getNextMidnight();

    return {
      questionsUsed,
      questionsRemaining,
      isLimited,
      resetTime,
      isUnlocked: false
    };
  }

  /**
   * Attempt to unlock with password
   */
  public unlock(password: string): boolean {
    if (password === this.UNLOCK_PASSWORD) {
      localStorage.setItem(this.UNLOCK_KEY, 'true');
      console.log('🔓 Rate limit unlocked');
      return true;
    }
    return false;
  }

  /**
   * Lock again (for testing or if Maria wants to re-enable limits)
   */
  public lock(): void {
    localStorage.removeItem(this.UNLOCK_KEY);
    console.log('🔒 Rate limit re-enabled');
  }

  /**
   * Check if currently unlocked
   */
  public isUnlocked(): boolean {
    return localStorage.getItem(this.UNLOCK_KEY) === 'true';
  }

  /**
   * Get rate limit data from storage
   */
  private getRateLimitData(): { date: string; count: number } {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error reading rate limit data:', error);
    }

    // Default
    return {
      date: this.getTodayString(),
      count: 0
    };
  }

  /**
   * Save rate limit data to storage
   */
  private saveRateLimitData(data: { date: string; count: number }): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving rate limit data:', error);
    }
  }

  /**
   * Reset the counter (new day)
   */
  private reset(): void {
    this.saveRateLimitData({
      date: this.getTodayString(),
      count: 0
    });
  }

  /**
   * Check if we should reset (new day)
   */
  private shouldReset(storedDate: string): boolean {
    return storedDate !== this.getTodayString();
  }

  /**
   * Get today's date as string (YYYY-MM-DD)
   */
  private getTodayString(): string {
    const now = new Date();
    return now.toISOString().split('T')[0];
  }

  /**
   * Get next midnight time as readable string
   */
  private getNextMidnight(): string {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    return tomorrow.toLocaleString();
  }

  /**
   * Get the unlock password (for display to authorized users)
   */
  public getUnlockPassword(): string {
    return this.UNLOCK_PASSWORD;
  }
}
